import React from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Typography,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { useTranslation } from 'react-i18next';
import { generateDashboard } from '../../../utilityFunctions/dashboardApi';

// Dashboards.create.examples.* — each one only references tables/columns in the
// tenant metadata (visits.service_date/status/pantry_id, pantries.name,
// stats_services measures, alchemer_survey household_size/receives_snap/receives_wic)
// so the NL2SQL pipeline can actually answer it.
const EXAMPLE_KEYS = ['visitsByStatus', 'topPantries', 'peopleServed', 'surveyHouseholds'];

/**
 * "Create with AI": POST /generate → { dashboard_id, run_id }. On success the caller
 * navigates to the dashboard with `?run=<run_id>`, where RunProgressPanel shows the
 * plan approval and the widgets as they build.
 *
 * @param onStarted({ dashboardId, runId })
 */
export default function CreateDashboardDialog({ open, onClose, jwt, onStarted }) {
  const { t } = useTranslation();
  const [requestText, setRequestText] = React.useState('');
  const [title, setTitle] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    if (open) setError(null);
  }, [open]);

  const submit = async () => {
    const text = requestText.trim();
    if (!text || busy) return;
    setBusy(true);
    setError(null);
    const res = await generateDashboard(text, title.trim() || null, jwt);
    setBusy(false);
    if (res.ok && res.data && res.data.dashboard_id != null && res.data.run_id != null) {
      setRequestText('');
      setTitle('');
      onStarted({ dashboardId: res.data.dashboard_id, runId: res.data.run_id });
      return;
    }
    setError((res.data && res.data.message) || t('Dashboards.create.failed'));
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('Dashboards.create.title')}</DialogTitle>
      <DialogContent sx={{ overflowWrap: 'anywhere' }}>
        <TextField
          autoFocus
          label={t('Dashboards.create.requestLabel')}
          placeholder={t('Dashboards.create.requestPlaceholder')}
          value={requestText}
          onChange={(e) => setRequestText(e.target.value)}
          multiline
          minRows={3}
          maxRows={8}
          fullWidth
          margin="dense"
          disabled={busy}
        />
        <TextField
          label={t('Dashboards.create.titleLabel')}
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          fullWidth
          margin="dense"
          disabled={busy}
          inputProps={{ maxLength: 200 }}
        />
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5, mb: 0.5 }}>
          {t('Dashboards.create.examplesTitle')}
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'flex-start' }}>
          {EXAMPLE_KEYS.map((key) => {
            const ex = t(`Dashboards.create.examples.${key}`);
            return (
              <Chip key={key} size="small" variant="outlined" label={ex} onClick={() => setRequestText(ex)} disabled={busy}
                sx={{ maxWidth: '100%', height: 'auto', '& .MuiChip-label': { whiteSpace: 'normal', overflowWrap: 'anywhere', py: 0.5 } }} />
            );
          })}
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>{t('Dashboards.create.hint')}</Typography>
        {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>{t('Dashboards.common.cancel')}</Button>
        <Button variant="contained" onClick={submit} disabled={busy || !requestText.trim()}
          startIcon={busy ? <CircularProgress size={16} color="inherit" /> : <AutoAwesomeIcon />}>
          {busy ? t('Dashboards.create.starting') : t('Dashboards.create.submit')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
